import * as Business   from '../business';
import * as ENV        from '../util/env';
import * as Invoices   from '.';
import * as Payouts    from '../payouts';
import * as Phone      from 'libphonenumber-js';
import * as UI         from '../ui';
import * as User       from '../user';
import { connect }     from 'react-redux';
import { CSVLink }     from 'react-csv';
import { format }      from 'date-fns';
import { parseISO }    from 'date-fns';
import { useCallback } from 'react';
import { useState }    from 'react';
import Badge           from '../components/badge';
import ConfirmButton   from '../components/confirm_button';
import F7Event         from '../app/events/f7_event';
import FetchFromServer from '../components/fetch_from_server';
import InvoicesList    from './invoices_list';
import Link            from '../components/link';
import Navbar          from '../components/navbar';
import NotFoundPage    from '../components/not_found_page';
import PayoutsList     from '../payouts/payouts_list';
import PrimaryButton   from '../components/primary_button';
import React           from 'react';
import toMoneyFormat   from '../util/to_money_format';


const Invoice = {};
export default Invoice;


const paymentMethodsDisplayName = {
  creditCard: 'Credit Card',
  googlePay:  'Google Pay',
  applePay:   'Apple Pay',
  paypal:     'PayPal',
  venmo:      'Venmo',
  cash:       'Cash',
  check:      'Check',
  other:      'Other'
};


Invoice.InvoicesPage = connect(
  (state, { businessID }) => ({
    businessID,
    invoices:         Invoices.getInvoices(state, businessID),
    isAdmin:          User.isAdmin(state, businessID),
    isManager:        User.isManager(state, businessID),
    paymentsSettings: Business.getPaymentsSettings(state, businessID),
    payouts:          Payouts.getPayouts(state, businessID),
    invoicesStatuses: Invoices.getInvoicesStatuses(state, businessID),
    payoutsStatuses:  Payouts.getPayoutsStatuses(state, businessID)
  }),
  (dispatch, { businessID }) => ({
    loadInvoices: () => {
      dispatch(Invoices.readInvoices(businessID));
    },
    loadPayouts: () => {
      dispatch(Payouts.readPayouts(businessID));
    }
  })
)(InvoicesPageComponent);


export function InvoicesPageComponent({ businessID, invoices, isAdmin, isManager, payouts, invoicesStatuses, payoutsStatuses, loadInvoices, loadPayouts, paymentsSettings }) {
  const [ view, setView ]   = useState('invoices');
  const isPaymentsAvailable = paymentsSettings?.available;
  const isPaymentsLiveMode  = paymentsSettings?.liveMode;
  const stripeURL           = paymentsSettings?.stripeURL;

  if (isPaymentsAvailable && isManager) {
    return (
      <React.Fragment>
        <Navbar title="Invoices & Payouts">
          {!ENV.isCordova() && (view === 'invoices') && invoices.length && (
            <Navbar.RightButton>
              <ExportButton invoices={invoices}/>
            </Navbar.RightButton>
          )}
        </Navbar>
        {view === 'invoices' && <InvoicesWrapper statuses={invoicesStatuses} load={loadInvoices} {...{ businessID, invoices, setView }}/>}
        {view === 'payouts'  && <PayoutsWrapper statuses={payoutsStatuses} load={loadPayouts} {...{ businessID, payouts, setView, isPaymentsLiveMode, stripeURL, isAdmin }}/>}
      </React.Fragment>
    );
  } else
    return (<NotFoundPage/>);
}


function InvoicesWrapper({ setView, businessID, invoices, statuses, load }) {
  const { readStatus } = statuses;
  const isReady        = !readStatus.idle && !readStatus.pending;

  return (
    <FetchFromServer {...{ statuses, load }}>
      <div className="b-segments__container">
        <div className="segmented">
          <button data-segment="invoices" className="button button-active" onClick={() => setView('invoices')}>INVOICES</button>
          <button data-segment="payouts" className="button" onClick={() => setView('payouts')}>PAYOUTS</button>
        </div>
        {isReady && (<InvoicesList {...{ businessID, invoices }}/>)}
      </div>
    </FetchFromServer>
  );
}


function PayoutsWrapper({ setView, businessID, payouts, statuses, load, isPaymentsLiveMode, isAdmin, stripeURL }) {
  const { readStatus } = statuses;
  const isReady        = !readStatus.idle && !readStatus.pending;

  return (
    <FetchFromServer {...{ statuses, load }}>
      <div className="b-segments__container">
        <div className="segmented">
          <button data-segment="invoices" className="button" onClick={() => setView('invoices')}>INVOICES</button>
          <button data-segment="payouts" className="button button-active" onClick={() => setView('payouts')}>PAYOUTS</button>
        </div>
        {isReady && isPaymentsLiveMode && (<PayoutsList {...{ businessID, payouts }}/>)}
        {isReady && !isPaymentsLiveMode && (
          <section className="block">
            <Badge {...{ color: 'orange', text: 'Test Mode' }}/>
            <p>You are currently testing our Payments feature. Once a bank account is connected, this is where a list of payouts will appear.</p>
            {isAdmin && (
              <p>If you would like to start accepting payments for your business, <Link href={stripeURL} external>connect your bank account</Link>.</p>
            )}
          </section>
        )}
      </div>
    </FetchFromServer>
  );
}


function ExportButton({ invoices }) {
  const headers = [
    { label: 'ID', key: 'id' },
    { label: 'Date', key: 'date' },
    { label: 'Customer', key: 'customer' },
    { label: 'Amount', key: 'amount' },
    { label: 'Tip', key: 'tip' },
    { label: 'Total Paid', key: 'totalPaid' },
    { label: 'Paid Date', key: 'paidDate' },
    { label: 'Status', key: 'status' }
  ];

  const data = invoices.reduce((acc, invoice) => {
    const { payments } = invoice;
    const paidDate     = payments?.[0].timestamp;

    const csvInvoice = {
      id:        invoice.id,
      date:      format(parseDate(invoice.timestamp), 'yyyy/MM/dd'),
      customer:  invoice.customer.name.full,
      amount:    invoice.amount,
      tip:       invoice.tip,
      totalPaid: invoice.totalPaid,
      status:    invoice.status,
      paidDate:  paidDate && format(parseDate(paidDate), 'yyyy/MM/dd')
    };
    return [ ...acc, csvInvoice ];
  }, []);

  return (
    <CSVLink
      headers={headers}
      data={data}
      filename="invoices.csv"
      className="external link"
    >
      Export
    </CSVLink>
  );
}


// -- Invoice Detail section --

export function DetailPageComponent({ businessID, statuses, invoice, load, resend, markVoid, markAsPaid, refund, toast, isAdmin, isManager, paymentsSettings }) {
  const isPaymentsAvailable = paymentsSettings?.available;
  const invoiceMode         = invoice?.mode;
  const isLiveMode          = invoiceMode === 'live';

  if (isPaymentsAvailable && isManager) {
    return (
      <React.Fragment>
        <Navbar title="Invoice Detail"/>
        <FetchFromServer {...{
          statuses,
          load,
          notFoundAction: 'See all invoices',
          notFoundTab:    'metrics/invoices'
        }}>
          {invoice && (
            <div className="invoice-detail">
              {!isLiveMode && <Badge {...{ color: 'orange', text: 'Test Mode' }}/>}
              <DetailHeader invoice={invoice}/>
              <DateInfo invoice={invoice}/>
              <CustomerDetails businessID={businessID} invoice={invoice}/>
              <AssistingTeamMemberDetails invoice={invoice}/>
              <TotalInfo invoice={invoice}/>
              <PaymentInfo invoice={invoice}/>
              <ActionButtons {...{
                invoice,
                toast,
                resend,
                markVoid,
                markAsPaid,
                refund,
                isAdmin
              }}/>
              <DetailFooter invoice={invoice}/>
            </div>
          )}
        </FetchFromServer>
      </React.Fragment>
    );
  } else
    return (<NotFoundPage/>);
}


Invoice.DetailPage = connect(
  (state, { businessID, invoiceID }) => ({
    businessID,
    isAdmin:          User.isAdmin(state, businessID),
    isManager:        User.isManager(state, businessID),
    paymentsSettings: Business.getPaymentsSettings(state, businessID),
    invoice:          Invoices.getInvoice(state, invoiceID),
    statuses:         [
      Invoices.getInvoiceStatuses(state, invoiceID)
    ]
  }),
  (dispatch, { businessID, invoiceID }) => ({
    load: () => {
      dispatch(Invoices.readInvoice(invoiceID));
    },
    resend:     () => dispatch(Invoices.resend(invoiceID)),
    markAsPaid: (paymentMethod, invoice) => dispatch(Invoices.markAsPaid({ businessID, paymentMethod, invoice })),
    markVoid:   invoice => dispatch(Invoices.markVoid(businessID, invoice)),
    refund:     invoice => dispatch(Invoices.refund(businessID, invoice)),
    toast:      msg => dispatch(UI.toast(msg))
  })
)(DetailPageComponent);


function DetailHeader({ invoice }) {
  const { number } = invoice;
  const { status } = invoice;

  return (
    <div className="row header-row detail-section no-gap">
      <div className="col-70 invoice-id">Invoice #{number}</div>
      <div className="col-30 content-flex-end">
        <div className={`badge ${status}-badge`}>
          <BadgeIcon status={status}/>
          {status.toUpperCase()}
        </div>
      </div>
    </div>
  );
}


function BadgeIcon({ status }) {
  switch (status) {
    case 'paid':
      return <i className="icons8 icons8-green-check-mark"/>;
    case 'new':
      return <i className="f7-icons">document_text</i>;
    case 'sent':
      return <i className="f7-icons">arrow_up</i>;
    case 'void':
      return <i className="f7-icons">close</i>;
    case 'refunded':
      return <i className="f7-icons">reload</i>;
    default:
      return null;
  }
}


function DateInfo({ invoice }) {
  const { message }   = invoice;
  const { timestamp } = invoice;

  return (
    <div className="row detail-section no-gap">
      <div className="col-50">
        <span>Created: {format(parseDate(timestamp), 'MM/dd/yyyy')}</span>
      </div>
      {message?.timestamp &&
        <div className="col-50 content-flex-end">
          <span>Sent: {format(parseDate(message.timestamp), 'MM/dd/yyyy')}</span>
        </div>
      }
    </div>
  );
}


function CustomerDetails({ businessID, invoice }) {
  const { customer }        = invoice;
  const { id : customerID } = customer;
  const customerLink        = `/${businessID}/contacts/${customerID}`;
  const customerName        = getInvoiceCustomerName({ customer });
  const { contact }         = customer;
  const contactInfo         = contact && getContactContactDetails({ contact });

  return (
    <div className="detail-section customer-detail">
      <span className="detail-section-title">Customer</span>
      {customerID && <a href={customerLink}>{customerName}</a>}
      {!customerID && <span>{customerName}</span>}
      {contactInfo && <span>{contactInfo}</span>}
    </div>
  );
}


function AssistingTeamMemberDetails({ invoice }) {
  const { createdBy } = invoice;
  const { name }      = createdBy;

  return (
    <div className="detail-section assisting-team-member-detail">
      <span className="detail-section-title">Assisting Team Member</span>
      <span>{name}</span>
    </div>
  );
}


function getContactContactDetails({ contact }) {
  const email          = contact.email?.address;
  const phone          = contact.phone?.number;
  const formattedPhone = phone && Phone.format(phone, 'National');

  if (email && formattedPhone)
    return `${email} - ${formattedPhone}`;

  return email || formattedPhone;
}


function TotalInfo({ invoice }) {
  const { description } = invoice;
  const { amount }      = invoice;
  const { tip }         = invoice;
  const { totalPaid }   = invoice;
  const { type }        = invoice;
  const isDeposit       = type === 'deposit';

  return (
    <div className="detail-section">
      <span className="detail-section-title row">Details</span>
      <div className="row no-gap">
        <div className="col-50">
          {isDeposit  && `${description} (Deposit)`}
          {!isDeposit && description}
        </div>
        <div className="col-50 content-flex-end">
          {toMoneyFormat({ amount })}
        </div>
      </div>
      {tip &&
        <div className="row no-gap">
          <div className="col-50">
            Tip
          </div>
          <div className="col-50 content-flex-end">
            {toMoneyFormat({ amount: tip })}
          </div>
        </div>
      }
      <hr/>
      <div className="row no-gap">
        <div className="col-50">
          Total
        </div>
        <div className="col-50 content-flex-end">
          {!totalPaid && toMoneyFormat({ amount })}
          {totalPaid && toMoneyFormat({ amount: totalPaid })}
        </div>
      </div>
    </div>
  );
}


function PaymentInfo({ invoice }) {
  const { status } = invoice;
  const isPaid     = status === 'paid';


  if (!isPaid)
    return null;

  const { payments }      = invoice;
  const firstPayment      = payments[0];
  const { timestamp }     = firstPayment;
  const parsedDate        = parseDate(timestamp);
  const { method }        = firstPayment;
  const { cardType }      = firstPayment;
  const { last4 }         = firstPayment;
  const methodDisplayName = paymentMethodsDisplayName[method] || method;

  return (
    <div className="detail-section payment-information">
      <span className="detail-section-title row">Payment Information</span>
      <div className="row">
        <span>Date: {format(parsedDate,  'MM/dd/yyyy')}</span>
      </div>
      <div className="row">
        <span>
          Method: {methodDisplayName}
          {cardType && ` (${cardType} - ${last4})`}
        </span>
      </div>
    </div>
  );
}


function ActionButtons({ invoice, resend, markVoid, markAsPaid, refund, toast, isAdmin }) {
  const { processor }        = invoice;
  const { status }           = invoice;
  const showSendButton       = status !== 'void' && status !== 'new';
  const showRefundButton     = isAdmin && status === 'paid' && processor === 'stripe';
  const showMarkAsPaidButton = status === 'new' || status === 'sent';
  const showVoidButton       = status === 'new' || status === 'sent';

  async function onResend() {
    await resend();
    if (status === 'paid')
      toast('The receipt was succesfully re-sent');
    else
      toast('The invoice was succesfully re-sent');
  }

  function openActionSheet({ target }) {
    target.dispatchEvent(new F7Event(app => {
      const paymentMethodsForMarkAsPaid = [ 'creditCard', 'cash', 'check', 'other' ];
      const buttons                     = paymentMethodsForMarkAsPaid.map(paymentMethod => {
        return {
          text: paymentMethodsDisplayName[paymentMethod],
          onClick() {
            markAsPaid(paymentMethod, invoice);
          }
        };
      });

      const actionSheet = app.actions.create({
        buttons: [
          ...buttons,
          {
            text:  'Cancel',
            color: 'red'
          }
        ]
      });

      actionSheet.open();
    }));
  }

  function onMarkVoid() {
    markVoid(invoice);
  }

  const refundPayment = useCallback(() => {
    refund(invoice);
  }, [ invoice, refund ]);

  return (
    <div className="action-buttons">
      {showSendButton && <PrimaryButton label="Resend" className="send-button" onClick={onResend} fill={false}/>}
      {showRefundButton && <RefundPaymentButton {...{ invoice, refundPayment }}/>}
      {showMarkAsPaidButton && <PrimaryButton label="Mark as Paid" className="mark-as-paid-button" onClick={openActionSheet} fill={false}/>}
      {showVoidButton && <PrimaryButton label="Void" className="void-button" onClick={onMarkVoid} fill={false} danger/>}
    </div>
  );
}


function RefundPaymentButton({ invoice, refundPayment }) {
  const { totalPaid } = invoice;
  const { customer }  = invoice;
  const customerName  = getInvoiceCustomerName({ customer });

  return (
    <ConfirmButton {...{
      label:           'Refund',
      className:       'refund-button',
      onConfirm:       refundPayment,
      confirmTitle:    'Refund Payment',
      confirmSubtitle: `Refund ${toMoneyFormat({ amount: totalPaid })} to ${customerName}?`
    }}/>
  );
}


function DetailFooter({ invoice }) {
  const { payURL } = invoice;
  const { status } = invoice;
  const isPaid     = status === 'paid';
  const linkText   = isPaid ? 'View Receipt' : 'Open Payment Page';
  return (
    <div className="detail-section detail-footer">
      <div className="row no-gap">
        <div className="col">
          <Link href={payURL} className="open-pay-url" external>
            <i className="f7-icons">exit</i>
            {linkText}
          </Link>
        </div>
      </div>
    </div>
  );
}


function getInvoiceCustomerName({ customer }) {
  return customer?.name?.full ?? null;
}


function parseDate(date) {
  return typeof date === 'string' ? parseISO(date) : date;
}
